import { type CountryValue, toShortCountry } from "@offstreetparking/geography";
import type { CountryCode } from "libphonenumber-js";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mergeRefs } from "../../utils/merge-refs";
import { BaseInput } from "../Input";
import type { InputProps } from "../Input";
import { CountrySelect } from "./PhoneCountrySelect";
import { type Libphonenumber, useLibPhoneNumber } from "./useLibPhoneNumber";
import { countDigitsBefore, findIndexOfNthDigit, usePhoneFormatter } from "./usePhoneFormatter";

export type BasePhoneInputProps = Omit<InputProps, "value" | "onChange" | "ref" | "defaultValue"> & {
	defaultCountry?: CountryValue | string | null;
	value: string;
	onChange: (value?: string) => void;
};

const BasePhoneInputInner = (props: BasePhoneInputProps, ref: React.ForwardedRef<HTMLInputElement>) => {
	const lib = useLibPhoneNumber();
	if (!lib) {
		return <BasePhoneInputFallback {...props} />;
	}
	return <BasePhoneInputReady {...props} lib={lib} ref={ref} />;
};

BasePhoneInputInner.displayName = "BasePhoneInput";

export const BasePhoneInput = forwardRef(BasePhoneInputInner);

const BasePhoneInputFallback = ({
	className,
	size,
	name,
	// Strip custom/prop-only fields so they don't leak onto the DOM via `...rest`.
	defaultCountry: _defaultCountry,
	value: _value,
	onChange: _onChange,
	onClear: _onClear,
	...rest
}: BasePhoneInputProps) => (
	<div className="flex">
		<CountrySelect
			data-testid={`${name}-country-select`}
			lib={null}
			value=""
			size={size}
			className={className}
			disabled
		/>
		<BaseInput
			{...rest}
			name={name}
			type="tel"
			disabled
			size={size}
			value=""
			className="w-full grow rounded-l-none"
		/>
	</div>
);

type BasePhoneInputReadyProps = BasePhoneInputProps & { lib: Libphonenumber };

const BasePhoneInputReadyInner = (
	{ lib, defaultCountry, className, size, disabled, readOnly, ...props }: BasePhoneInputReadyProps,
	ref: React.ForwardedRef<HTMLInputElement>,
) => {
	const [country, setCountry] = useState<CountryCode | undefined>(() => {
		if (props.value) {
			const parsedCountry = lib.parsePhoneNumberFromString(props.value)?.country;
			if (parsedCountry) {
				return parsedCountry;
			}
		}
		if (defaultCountry) {
			return toShortCountry(defaultCountry) as CountryCode;
		}
		return undefined;
	});

	const { format } = usePhoneFormatter(lib, country);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const cursorPosRef = useRef<number | null>(null);
	const isComposingRef = useRef(false);
	const lastEmittedValueRef = useRef(props.value);
	const onChangeRef = useRef(props.onChange);
	onChangeRef.current = props.onChange;

	// When `value` can't be parsed (e.g. user previously entered an invalid number), preserve
	// their digits so the input stays editable instead of resetting to empty.
	const formatRawDigits = (value: string, fallbackCountry: CountryCode): string => {
		const digits = value.replace(/\D/g, "");
		const callingCode = lib.getCountryCallingCode(fallbackCountry);
		const trimmed = digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits;
		return format(trimmed).formatted || trimmed;
	};

	const [displayValue, setDisplayValue] = useState(() => {
		if (!props.value || !country) {
			return "";
		}
		const parsed = lib.parsePhoneNumberFromString(props.value);
		if (parsed) {
			return format(parsed.nationalNumber).formatted;
		}
		return formatRawDigits(props.value, country);
	});

	// Sync display value and country when the external value changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: formatRawDigits closes over lib + format which are already deps
	useEffect(() => {
		if (props.value === lastEmittedValueRef.current) {
			return;
		}

		lastEmittedValueRef.current = props.value;

		if (!props.value) {
			setDisplayValue("");
			return;
		}

		const parsed = lib.parsePhoneNumberFromString(props.value);
		if (parsed) {
			if (parsed.country && parsed.country !== country) {
				setCountry(parsed.country);
			}
			setDisplayValue(format(parsed.nationalNumber).formatted);
			return;
		}

		if (!country) {
			setDisplayValue("");
			return;
		}

		setDisplayValue(formatRawDigits(props.value, country));
	}, [lib, props.value, country, format]);

	// Restore cursor position after formatting — displayValue is intentionally
	// in the dep array as a trigger even though it's not read inside the effect
	// biome-ignore lint/correctness/useExhaustiveDependencies: displayValue triggers cursor restore after re-render
	useEffect(() => {
		if (cursorPosRef.current !== null && inputRef.current) {
			const pos = cursorPosRef.current;
			requestAnimationFrame(() => {
				if (inputRef.current) {
					inputRef.current.setSelectionRange(pos, pos);
				}
			});
			cursorPosRef.current = null;
		}
	}, [displayValue]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (!country) {
				return;
			}

			if (isComposingRef.current) {
				setDisplayValue(e.target.value);
				return;
			}

			const rawInput = e.target.value;
			const cursorPosition = e.target.selectionStart ?? rawInput.length;

			let digits = rawInput.replace(/\D/g, "");

			if (!digits) {
				lastEmittedValueRef.current = "";
				setDisplayValue("");
				onChangeRef.current("");
				cursorPosRef.current = 0;
				return;
			}

			const callingCode = lib.getCountryCallingCode(country);
			if (digits.startsWith(callingCode) && digits.length > callingCode.length + 1) {
				digits = digits.slice(callingCode.length);
			}

			const { formatted, e164 } = format(digits);

			const digitsBeforeCursor = countDigitsBefore(rawInput, cursorPosition);
			const newCursorPos = findIndexOfNthDigit(formatted, digitsBeforeCursor);

			cursorPosRef.current = newCursorPos;
			setDisplayValue(formatted);

			const emitValue = e164 && e164.replace("+", "") !== callingCode ? e164 : "";

			lastEmittedValueRef.current = emitValue;
			onChangeRef.current(emitValue);
		},
		[lib, country, format],
	);

	const handleCompositionStart = useCallback(() => {
		isComposingRef.current = true;
	}, []);

	const handleCompositionEnd = useCallback(
		(e: React.CompositionEvent<HTMLInputElement>) => {
			isComposingRef.current = false;
			handleChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
		},
		[handleChange],
	);

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === " ") {
			e.preventDefault();
		}
	}, []);

	const handleCountryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
		const newCountry = e.target.value ? (e.target.value as CountryCode) : undefined;
		setCountry(newCountry);
		setDisplayValue("");
		lastEmittedValueRef.current = "";
		onChangeRef.current("");
	}, []);

	const setRef = useMemo(() => mergeRefs(inputRef, ref), [ref]);

	return (
		<div className="flex">
			<CountrySelect
				data-testid={`${props.name}-country-select`}
				lib={lib}
				value={country}
				size={size}
				onChange={handleCountryChange}
				className={className}
				disabled={disabled || readOnly}
			/>
			<BaseInput
				{...props}
				ref={setRef}
				type="tel"
				disabled={disabled || !country}
				readOnly={readOnly}
				size={size}
				value={displayValue}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				onCompositionStart={handleCompositionStart}
				onCompositionEnd={handleCompositionEnd}
				className="w-full grow rounded-l-none"
			/>
		</div>
	);
};

const BasePhoneInputReady = forwardRef(BasePhoneInputReadyInner);
